import Vue from 'vue';
import Router from 'vue-router';
import store from '@store';
import routesAuth from './routes/auth';
import routesAdmin from './routes/admin';
import { getDeepObj, isObject } from '@utils/format';
import NProgress from 'nprogress/nprogress';

NProgress.configure({ showSpinner: false });

Vue.use(Router);

/**
 * Konstanta & helper di luar guard (tidak dibuat ulang tiap navigasi)
 */
const SKIP_LOGIN_CHECK_ROUTE_NAMES = new Set(['404', 'sesi-habis', 'aktivasi', 'forget', 'reset', 'daftar']); // tetap boleh tanpa login

const OPERATOR_TYPES = ['tenant', 'organisasi', 'lokasi', 'loket'];
const OP_CODE = { tenant: 'tn', organisasi: 'og', lokasi: 'lk', loket: 'lt' };
const CODE_TO_TYPE = { tn: 'tenant', og: 'organisasi', lk: 'lokasi', lt: 'loket' };

function firstOperator(preference) {
  for (const jenis of OPERATOR_TYPES) {
    const arr = getDeepObj(preference, `operators.${jenis}`);
    if (arr && arr.length) {
      const detail = arr[0];
      const id = detail && getDeepObj(detail, `attributes.${jenis}_id`);
      return { operator: OP_CODE[jenis], id, detail };
    }
  }
  return { operator: '', id: '', detail: null };
}

function findOperatorDetail(preference, operatorCode, id) {
  const jenis = CODE_TO_TYPE[operatorCode];
  if (!jenis) return null;

  const data = getDeepObj(preference, `operators.${jenis}`) || [];
  const idx = data.findIndex((item) => Number(getDeepObj(item, `attributes.${jenis}_id`)) === Number(id));
  return idx >= 0 ? getDeepObj(data, `${idx}`) : null;
}

function redirectToLogin(next) {
  const isUcm = store.getters['auth/isUcm'];
  if (isUcm) {
    window.location.href = process.env.VUE_APP_API_URL + `/auth/cas`;
    return; // penting: stop flow
  }
  next({ name: 'login' });
}

function buildRoutesByRole(role) {
  // role admin dapat routesAdmin + routesAuth
  if (role === 'admin') return [...routesAdmin, ...routesAuth];
  // selain itu minimal routesAuth
  return [...routesAuth];
}

function createRouter() {
  return new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [...routesAuth], // default minimal; nanti bisa di-add setelah login
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) return savedPosition;
      return { x: 0, y: 0 };
    },
  });
}

const router = createRouter();

// Flag supaya addRoutes tidak dilakukan berulang-ulang
let routesInitialized = false;

export function resetRouter() {
  const newRouter = createRouter();
  router.matcher = newRouter.matcher;
  router.options.routes = [...routesAuth];
  routesInitialized = false;
}

/**
 * Pastikan routes sesuai role terpasang (sekali saja setelah user valid)
 */
async function ensureRoutesForUser() {
  if (routesInitialized) return false;

  const role = store.getters['auth/role'] || store.state.auth?.role; // fleksibel
  const modules = buildRoutesByRole(role);

  resetRouter();
  router.addRoutes(modules);
  routesInitialized = true;
  return true;
}

router.beforeEach(async (to, from, next) => {
  if (from.name !== null) NProgress.start();

  try {
    // 1) Route publik yang tidak perlu login (kecuali login sendiri ditangani di bawah)
    if (SKIP_LOGIN_CHECK_ROUTE_NAMES.has(to.name)) return next();

    // 2) Kalau belum login dan memang menuju login => ok
    const loggedIn = store.getters['auth/loggedIn'];

    if (!loggedIn && to.name === 'login') return next();

    /**
     * 3) Validasi akun sekali saja
     *    checkAkun dianggap akan set state loggedIn
     */
    const isUserLogin = await store.dispatch('auth/checkAkun');
    const hasAkun = store.getters['auth/loggedIn'];

    if (!isUserLogin || !hasAkun) {
      return redirectToLogin(next);
    }

    // 4) Pastikan dynamic routes terpasang sesuai role (sekali)
    if (await ensureRoutesForUser()) {
      return next({ ...to, replace: true });
    }

    // 5) Ambil preference sekali
    const preference = store.getters['preferensi/preference'] || {};
    if (!preference) return next({ name: '404' });

    const langganan = preference.langganan || {};

    // Prevent loop: checking langganan shouldn't redirect if we are already there
    if (to.name === 'berlangganan') return next();

    const needSubscribeRedirect = !isObject(langganan) || (langganan && langganan.k_status_berkas !== 5);

    if (needSubscribeRedirect) return next({ name: 'berlangganan' });

    // 6) Resolve operator & id
    let operator = to.params.operator || '';
    let id = to.params.id || '';

    // kalau route minta operator+id tapi invalid, fallback ke firstOperator
    let detail = null;
    if (operator && id) detail = findOperatorDetail(preference, operator, id);

    if (!detail) {
      const first = firstOperator(preference);
      operator = first.operator;
      id = first.id;
      detail = first.detail;
    }

    // Kalau tetap tidak ada operator/id yang valid
    if (!operator || !id || !detail) return next({ name: 'berlangganan' });

    // 7) Set loginAs (await supaya konsisten; kalau tidak perlu bisa tanpa await)
    await store.dispatch('preferensi/setloginAs', {
      id,
      operator: CODE_TO_TYPE[operator],
      as: detail,
    });

    // 8) Kalau route saat ini belum punya param id, lempar ke home dengan param
    if (!to.params.id) {
      return next({ name: 'home', params: { operator, id } });
    }

    return next();
  } catch (err) {
    // Error tak terduga di sini (mis. checkAkun gagal karena sesi habis / 401) diarahkan
    // ke halaman "sesi habis" (bukan login langsung) agar tidak loop login tanpa sebab.
    return next({ name: 'sesi-habis', query: { redirect: to.fullPath } });
  }
});

/**
 * beforeResolve tetap boleh dipakai untuk meta.beforeResolve,
 * tapi pastikan error handling tidak "silent" berlebihan.
 */
router.beforeResolve(async (to, from, next) => {
  try {
    for (const route of to.matched) {
      if (!route.meta || !route.meta.beforeResolve) continue;

      await new Promise((resolve, reject) => {
        route.meta.beforeResolve(to, from, (...args) => {
          if (args.length) {
            if (from.name === args[0].name) NProgress.done();
            next(...args);
            return reject(new Error('Redirected'));
          }
          resolve();
        });
      });
    }

    next();
  } catch (e) {
    // kalau redirect via meta.beforeResolve, promise reject => stop.
    // jangan panggil next lagi.
  }
});

router.afterEach(() => {
  NProgress.done();
  store.commit('SET_LOADING', false);
});

export default router;
